import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MarkerActionTypes,
  SELECTOR_MARKER_CURRENT_MARKER,
  SELECTOR_MARKER_MARKERS,
  SELECTOR_MARKER_SELECTED_MARKER_ID,
  SELECTOR_MARKER_STATUS,
  SELECTOR_MARKER_IS_MARKER_OVERLAY_VISIBLE,
  SELECTOR_MARKER_IS_MARKER_PREVIEW_ENABLED,
} from 'src/store/reducers/marker';
import { MARKER_STATUS } from 'src/interfaces/enums';
import { IMarker } from 'src/interfaces/Marker';
import ApplicationService from 'src/services/ApplicationService';
import { DatahubEntity, MarkerAction } from 'src/interfaces/Datahub';
import { detectMobile } from 'src/utils/browserUtils';
import useMediaQuery from 'src/hooks/useMediaQuery';
import useWatchTime from '../useWatchTime';
import useTracking from '../useTracking';
import useApplicationData from '../useApplicationData';
import { generateTempId } from './utils/generateTempId';
import { MIN_SCREEN_WIDTH_ENOUGH_TO_SHOW_NOTES } from './constants';

interface ISaveMarkerProps {
  id?: IMarker['id'];
  time?: number;
  numberOfAttempts?: number;
  text: string;
}

const useMarker = () => {
  const markers = useSelector(SELECTOR_MARKER_MARKERS);
  const currentMarker = useSelector(SELECTOR_MARKER_CURRENT_MARKER);
  const status = useSelector(SELECTOR_MARKER_STATUS);
  const selectedMarkerId = useSelector(SELECTOR_MARKER_SELECTED_MARKER_ID);
  const isMarkerOverlayVisible = useSelector(SELECTOR_MARKER_IS_MARKER_OVERLAY_VISIBLE);
  const isMarkerPreviewEnabled = useSelector(SELECTOR_MARKER_IS_MARKER_PREVIEW_ENABLED);

  const { matchScreenSize: isScreenWidthEnoughToShowNotes } = useMediaQuery({
    min: MIN_SCREEN_WIDTH_ENOUGH_TO_SHOW_NOTES,
  });

  const { currentTime } = useWatchTime();
  const { trackDatahub } = useTracking();
  const { flags, isMediaPublic } = useApplicationData();
  const isMobile = detectMobile();

  const dispatch = useDispatch();

  const updateStatus = useCallback((status) => {
    dispatch({ type: MarkerActionTypes.UPDATE_STATUS, payload: status });
  }, []);

  const hideMarkerOverlay = useCallback(() => {
    dispatch({
      type: MarkerActionTypes.HIDE_MARKER_OVERLAY,
    });

    updateStatus(MARKER_STATUS.CLOSED);
  }, [dispatch, updateStatus]);

  useEffect(() => {
    if (!isScreenWidthEnoughToShowNotes) {
      hideMarkerOverlay();
    }
  }, [isScreenWidthEnoughToShowNotes, hideMarkerOverlay]);

  const showMarkerOverlay = useCallback(() => {
    dispatch({
      type: MarkerActionTypes.SHOW_MARKER_OVERLAY,
    });
  }, [dispatch]);

  const insertPendingMarker = useCallback(
    ({ id, text, time: mediaTime }: ISaveMarkerProps) => {
      const time = mediaTime ?? (currentTime || 0);
      const tempId = id ?? generateTempId();

      const pendingMarker = {
        text,
        time,
        updateDate: new Date().toISOString(),
        id: tempId,
      };

      dispatch({ type: MarkerActionTypes.ADD_MARKER, payload: pendingMarker });
      return tempId;
    },
    [currentTime, dispatch],
  );

  const removePendingMarker = useCallback(
    (id: IMarker['id']) => {
      dispatch({ type: MarkerActionTypes.REMOVE_MARKER, payload: id });
    },
    [dispatch],
  );

  const saveNewMarker = useCallback(
    async ({ text, time: mediaTime }: ISaveMarkerProps) => {
      const time = mediaTime ?? (currentTime || 0);
      const marker = await ApplicationService.marker.insertMarker({
        time,
        text,
      });

      dispatch({ type: MarkerActionTypes.ADD_MARKER, payload: marker });
      trackDatahub(DatahubEntity.MARKER, MarkerAction.CREATE, text);
    },
    [currentTime, dispatch, trackDatahub],
  );

  const saveUpdatedMarker = useCallback(
    async ({ text, id, time: mediaTime }: ISaveMarkerProps) => {
      await ApplicationService.marker.updateMarker({
        time: mediaTime ?? (currentTime || 0),
        text,
        id,
      });

      dispatch({ type: MarkerActionTypes.UPDATE_MARKER, payload: { id, text } });
    },
    [currentTime, dispatch],
  );

  const updateCurrentMarkerInStore = useCallback((marker) => {
    dispatch({ type: MarkerActionTypes.SET_CURRENT_MARKER, payload: marker });
  }, []);

  const handleUpdateMarker = useCallback((marker: IMarker) => {
    updateStatus(MARKER_STATUS.EDITING);
    updateCurrentMarkerInStore(marker);
    showMarkerOverlay();
  }, []);

  const updateSelectedMarkerId = useCallback((id: number | null) => {
    dispatch({ type: MarkerActionTypes.SET_SELECTED_MARKER_ID, payload: id });
  }, []);

  const deleteMarker = useCallback(
    async (id: IMarker['id']) => {
      await ApplicationService.marker.deleteMarker(id);
      dispatch({ type: MarkerActionTypes.REMOVE_MARKER, payload: id });
    },
    [dispatch],
  );

  const togglePreview = useCallback(() => {
    ApplicationService.userPreferences.updatePreferences({ markerPreviewEnabled: !isMarkerPreviewEnabled });

    dispatch({ type: MarkerActionTypes.SET_MARKER_PREVIEW, payload: !isMarkerPreviewEnabled });
  }, [isMarkerPreviewEnabled]);

  const allowedShowMarker = (marker: IMarker) => {
    const formattedTime = Math.trunc(currentTime);

    return (
      (isMarkerPreviewEnabled &&
        marker.time >= formattedTime &&
        marker.time < formattedTime + 3 &&
        status !== MARKER_STATUS.EDITING &&
        selectedMarkerId === null) ||
      selectedMarkerId === marker.id
    );
  };

  const isNotesAllowed = useCallback(() => {
    return (
      !isMobile &&
      !isMediaPublic &&
      !isMarkerOverlayVisible &&
      flags.ENABLE_PLAYER_COSMOS_NOTES &&
      isScreenWidthEnoughToShowNotes
    );
  }, [
    flags.ENABLE_PLAYER_COSMOS_NOTES,
    isMarkerOverlayVisible,
    isMobile,
    isMediaPublic,
    isScreenWidthEnoughToShowNotes,
  ]);

  return {
    isMarkerOverlayVisible,
    showMarkerOverlay,
    hideMarkerOverlay,
    currentMarker,
    status,
    updateStatus,
    insertPendingMarker,
    removePendingMarker,
    deleteMarker,
    markers,
    handleUpdateMarker,
    updateSelectedMarkerId,
    selectedMarkerId,
    allowedShowMarker,
    togglePreview,
    isMarkerPreviewEnabled,
    saveNewMarker,
    saveUpdatedMarker,
    isNotesAllowed,
  };
};

export default useMarker;
